import dayjs from "dayjs";

/**
 * 格式化日期为 mm/dd hh:mm 格式
 * @param date - 要格式化的日期，可以是 Date 对象、时间戳或日期字符串
 * @returns 格式化后的日期字符串，格式为 mm/dd hh:mm
 */
export function formatToMMDDHHMM(date: Date | string | number): string {
  const dateObj = new Date(date);

  // 检查日期是否有效
  if (isNaN(dateObj.getTime())) {
    return "";
  }

  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${month}/${day} ${hours}:${minutes}`;
}

/**
 * 格式化当前时间为 mm/dd hh:mm 格式
 * @returns 当前时间的格式化字符串
 */
export function formatCurrentTime(): string {
  return formatToMMDDHHMM(new Date());
}

/**
 * 根据日期与当前时间的差异，智能格式化日期
 * 1. 如果是今天，格式为 Today HH:MM
 * 2. 如果是今年，格式为 MM/DD HH:MM
 * 3. 如果是更早的年份，格式为 YY/MM/DD HH:MM
 * @param date - 要格式化的日期，可以是 Date 对象、时间戳或日期字符串
 * @returns 格式化后的日期字符串
 */
export function formatDateSmart(date: Date | string | number): string {
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "";
  }

  const now = new Date();
  const isToday = now.toDateString() === dateObj.toDateString();
  const isThisYear = now.getFullYear() === dateObj.getFullYear();

  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  if (isToday) {
    return `Today ${hours}:${minutes}`;
  }

  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  if (isThisYear) {
    return `${month}/${day} ${hours}:${minutes}`;
  }

  const year = String(dateObj.getFullYear()).slice(-2);
  return `${year}/${month}/${day} ${hours}:${minutes}`;
}

/**
 * 格式化日期为YYYY-MM-DD字符串
 * @param date 日期对象
 * @returns 格式化后的字符串
 */
export function formatDateToYYYYMMDD(date: Date | string | number): string {
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "";
  }
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 格式化日期为YYYY-MM-DD HH:MM:SS格式
 * @param date - 要格式化的日期，可以是 Date 对象、时间戳或日期字符串
 * @returns 格式化后的日期字符串，格式为YYYY-MM-DD HH:MM:SS
 */
export function formatToFullDateTime(date: Date | string | number): string {
  const dateObj = new Date(date);

  // 检查日期是否有效
  if (isNaN(dateObj.getTime())) {
    return "";
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const seconds = String(dateObj.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * 格式化创建时间（超过一年展示完整时间，否则展示月日小时分钟）
 * @param date - 要格式化的日期字符串
 * @returns 格式化后的日期字符串
 */
export function formatTimeByYear(date: string): string {
  const createDate = dayjs(date);
  const now = dayjs();
  const yearsDiff = now.diff(createDate, 'year');

  return createDate.format(yearsDiff > 0 ? 'YYYY-MM-DD HH:mm' : 'MM-DD HH:mm');
}